import type { IFieldOverrides } from '@/context/DebugPanelContext';
import type { IDocumentTable } from '@models/Forms/IForms';

/**
 * Применяет дебаг-оверрайды правил к объекту колонки таблицы: возвращает клон, у которого
 * `readonly`/`required`/`visibilityRules`/`min`/`max`/`pattern`/`maxLength` подменены значениями из
 * `fieldOverrides` (в тех же строковых конвенциях, что читает грид: `'true'`/`'false'`, числа строкой).
 * Так все downstream-чтения `column.*` в TableData/EditTable подхватывают оверрайд без правки каждого места.
 * Возвращает исходный объект, если оверрайдов для колонки нет (не плодит лишних перерисовок).
 */
export function overrideColumn<T extends { key?: string }>(
    column: T,
    fieldOverrides: ReadonlyMap<string, IFieldOverrides>,
): T {
    const ov = column?.key ? fieldOverrides.get(column.key) : undefined;
    if (!ov) return column;

    const c = { ...column };
    const w = c as unknown as Record<string, unknown>;
    if (ov.readonly !== undefined) w.readonly = ov.readonly ? 'true' : 'false';
    if (ov.required !== undefined) w.required = ov.required ? 'true' : 'false';
    if (ov.visibility !== undefined) {
        w.visibilityRules = ov.visibility ? 'true' : 'false';
        if (ov.visibility) w.hidden = false; // раскрыть принудительно скрытую колонку
    }
    if (ov.min !== undefined) w.min = String(ov.min);
    if (ov.max !== undefined) w.max = String(ov.max);
    if (ov.pattern !== undefined) w.pattern = ov.pattern;
    if (ov.maxLength !== undefined) w.maxLength = ov.maxLength;
    return c;
}

/**
 * Клон таблицы с дебаг-оверрайдами, применёнными ко ВСЕМ типам колонок (и рекурсивно к подтаблицам) —
 * для потребителей валидации (`validateDocumentTable`), которые читают правила из схемы таблицы,
 * а не из отрендеренных колонок грида. Без оверрайдов возвращает исходный объект.
 */
export function overrideTableColumns(
    table: IDocumentTable,
    fieldOverrides: ReadonlyMap<string, IFieldOverrides> | undefined,
): IDocumentTable {
    if (!fieldOverrides?.size) return table;
    const mapCols = <T extends { key?: string }>(cols?: T[]): T[] | undefined =>
        cols?.map((col) => overrideColumn(col, fieldOverrides));
    return {
        ...table,
        tableColumn: mapCols(table.tableColumn)!,
        tableColumnDict: mapCols(table.tableColumnDict)!,
        tableColumnAbook: mapCols(table.tableColumnAbook)!,
        tableColumnCalc: mapCols(table.tableColumnCalc)!,
        tableColumnAutoComplete: mapCols(table.tableColumnAutoComplete)!,
        tables: table.tables?.map((sub) => overrideTableColumns(sub, fieldOverrides))!,
    };
}
